import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useGlobalDenseMode } from './useGlobalDenseMode';
import GlobalDenseModeToolbar from '../components/GlobalDenseModeToolbar';

const normalizeRows = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === 'object') {
    if (Object.keys(data).every((key) => !Number.isNaN(Number(key)))) {
      return Object.values(data);
    }
    if (data.data && typeof data.data === 'object') {
      return Object.values(data.data);
    }
  }
  return [];
};

const safeLocalRead = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

const safeLocalWrite = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
};

const csvEscape = (value) => {
  if (value === null || value === undefined) return '';
  const stringified = String(value);
  // Long digit-only identifiers (SSCC, pallet numbers, tracking numbers, UPCs) must be forced
  // to text: left unescaped or merely quoted, Excel still auto-detects them as numbers on open,
  // dropping leading zeros and rounding anything past ~15 digits into scientific notation,
  // silently corrupting the code. Wrapping in an ="..." formula is the standard way to make
  // Excel render the literal digits as text instead of coercing them.
  if (/^\d{10,}$/.test(stringified)) {
    return `"=""${stringified}"""`;
  }
  if (/[",\n]/.test(stringified)) return `"${stringified.replace(/"/g, '""')}"`;
  return stringified;
};

const buildCsv = (rows, columns, exportValueTransform) => {
  const headers = columns.map((col) => col.headerName ?? col.field);
  const lines = [headers.map(csvEscape).join(',')];
  rows.forEach((row) => {
    const rowValues = columns.map((col) => {
      const rawValue = row?.[col.field];
      if (typeof exportValueTransform === 'function') {
        return exportValueTransform(rawValue, row, col.field);
      }
      return rawValue;
    });
    lines.push(rowValues.map(csvEscape).join(','));
  });
  return lines.join('\n');
};

const downloadCsv = (filename, csv) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const highlightText = (content, searchTerm) => {
  if (
    !searchTerm ||
    typeof searchTerm !== 'string' ||
    !searchTerm.trim() ||
    content === null ||
    content === undefined
  ) {
    return content;
  }
  const term = searchTerm.trim();
  if (!term) return content;

  if (typeof content === 'string' || typeof content === 'number' || typeof content === 'boolean') {
    const str = String(content);
    const lowerStr = str.toLowerCase();
    const lowerTerm = term.toLowerCase();

    if (!lowerStr.includes(lowerTerm)) {
      return content;
    }

    const parts = [];
    let startIndex = 0;
    let matchIndex = lowerStr.indexOf(lowerTerm, startIndex);
    let keyIdx = 0;

    while (matchIndex !== -1) {
      if (matchIndex > startIndex) {
        parts.push(str.substring(startIndex, matchIndex));
      }
      const matchedText = str.substring(matchIndex, matchIndex + term.length);
      parts.push(
        <span
          key={`sm-${keyIdx++}`}
          className="search-match"
          style={{
            backgroundColor: '#ffeb3b',
            color: '#000000',
            borderRadius: '2px',
            padding: '0 1px',
            fontWeight: 600,
            display: 'inline',
          }}
        >
          {matchedText}
        </span>
      );
      startIndex = matchIndex + term.length;
      matchIndex = lowerStr.indexOf(lowerTerm, startIndex);
    }
    if (startIndex < str.length) {
      parts.push(str.substring(startIndex));
    }
    return <>{parts}</>;
  }

  if (Array.isArray(content)) {
    return content.map((item, idx) => (
      <React.Fragment key={idx}>{highlightText(item, searchTerm)}</React.Fragment>
    ));
  }

  if (React.isValidElement(content)) {
    if (content.props && content.props.children) {
      const newChildren = highlightText(content.props.children, searchTerm);
      return React.cloneElement(content, { ...content.props }, newChildren);
    }
  }

  return content;
};

let _canvas = null;
const measureTextWidth = (text, font = '13px Inter, -apple-system, Roboto, sans-serif') => {
  if (text === null || text === undefined || text === '') return 0;
  try {
    if (!_canvas) _canvas = document.createElement('canvas');
    const ctx = _canvas.getContext('2d');
    ctx.font = font;
    return ctx.measureText(String(text)).width;
  } catch {
    return String(text).length * 8;
  }
};

const FIELD_SEMANTIC_MIN_WIDTHS = {
  __check__: 50,
  __select_labels__: 60,
  priority: 85,
  total_qty: 85,
  totalqty: 85,
  quantity: 90,
  qty: 85,
  internal_id: 120,
  internalid: 120,
  id: 100,
  line_id: 105,
  lineid: 105,
  po: 125,
  po_number: 130,
  ponumber: 130,
  sonumber: 140,
  so_number: 140,
  transaction_id: 140,
  shipdate: 120,
  ship_date: 120,
  order_date: 120,
  carrier: 115,
  picker_name: 145,
  pickername: 145,
  status: 180,
  location: 175,
  customer_name: 200,
  customername: 200,
  customer_order: 190,
  customerorder: 190,
  ship_via: 190,
  shipvia: 190,
  tracking_number: 210,
  trackingnumber: 210,
  sscc_codes: 230,
  sscccodes: 230,
  generate_label: 140,
};

const computeColumnWidths = (col, tableData = []) => {
  const field = col.accessorKey || col.id || col.field || '';
  const headerStr = typeof col.header === 'string' ? col.header.trim() : (col.headerName || field || '').trim();

  // 1. Calculate minimum required width for header (including bold text, sort arrow, menu button, and cell padding)
  const headerTextWidth = Math.ceil(measureTextWidth(headerStr, '700 13px Inter, -apple-system, Roboto, sans-serif'));
  const headerMinWidth = headerStr ? Math.max(headerTextWidth + 48, 75) : 60;

  // 2. Sample top 50 rows of data to determine content width
  let maxContentWidth = 0;
  const sampleLimit = Math.min(tableData.length, 50);
  for (let i = 0; i < sampleLimit; i += 1) {
    const row = tableData[i];
    if (!row || typeof row !== 'object') continue;
    let val;
    if (typeof col.accessorFn === 'function') {
      val = col.accessorFn(row);
    } else if (field) {
      val = row[field];
    }
    if (val !== null && val !== undefined && val !== '') {
      const valStr = typeof val === 'object' ? JSON.stringify(val) : String(val);
      const measured = Math.ceil(measureTextWidth(valStr, '400 13px Inter, -apple-system, Roboto, sans-serif') + 30);
      if (measured > maxContentWidth) {
        maxContentWidth = measured;
      }
    }
  }

  // 3. Known field semantic minimum width
  const normalizedField = String(field).toLowerCase().replace(/[^a-z0-9]/g, '');
  const semanticMinWidth = FIELD_SEMANTIC_MIN_WIDTHS[normalizedField] || 0;

  // 4. Combine all factors into optimal base width and minWidth
  const calculatedWidth = Math.max(headerMinWidth, maxContentWidth, semanticMinWidth);
  const maxWidth = col.maxWidth || 400;
  const minWidth = Math.max(headerMinWidth, col.minWidth || (col.minSize && col.minSize !== 100 ? col.minSize : undefined) || 60);

  // If explicit width is provided (and not legacy generic constant 120), respect it; otherwise use optimal calculated width
  let resolvedWidth;
  if (col.width != null) {
    resolvedWidth = Math.max(col.width, minWidth);
  } else if (col.size && col.size !== 120) {
    resolvedWidth = Math.max(col.size, minWidth);
  } else {
    resolvedWidth = Math.min(Math.max(calculatedWidth, minWidth), maxWidth);
  }

  return {
    width: resolvedWidth,
    minWidth,
    maxWidth,
  };
};

const toDataGridColumns = (columns = [], findSearchTerm = '', tableData = []) =>
  columns.map((col, index) => {
    const field = col.accessorKey || col.id || `col_${index}`;
    const headerName = col.header || col.headerName || field;
    const { width: resolvedBaseWidth, minWidth: resolvedMinWidth } = computeColumnWidths(col, tableData);

    return {
      field,
      headerName,
      sortable: col.enableSorting !== false,
      hideable: true,
      resizable: col.resizable !== false && col.enableResizing !== false,
      width: resolvedBaseWidth,
      minWidth: resolvedMinWidth,
      ...(col.flex !== undefined ? { flex: col.flex } : {}),
      valueGetter: (params, row) => {
        const r = row ?? params?.row;
        if (typeof col.accessorFn === 'function') {
          return col.accessorFn(r);
        }
        const val = r?.[field];
        return val !== null && val !== undefined ? val : '';
      },
      getApplyQuickFilterFn: (value) => {
        if (!value) return null;
        const lowerValue = String(value).toLowerCase();
        return (params) => {
          const cellValue = params.value;
          if (cellValue === null || cellValue === undefined) return false;
          return String(cellValue).toLowerCase().includes(lowerValue);
        };
      },
      renderCell: (params) => {
        let content;
        if (col.cell) {
          content = col.cell({
            getValue: () => params.value,
            row: { original: params.row, index: params.rowIndex, id: params.id },
            column: { id: field },
          });
        } else {
          content = params.value;
        }
        if (!findSearchTerm) return content;
        return highlightText(content, findSearchTerm);
      },
      valueFormatter: col.formatDateTime
        ? (value) => col.formatDateTime(value)
        : undefined,
    };
  });

/**
 * Maps TanStack-style column defs + app table props into MUI X DataGridPro props.
 * Use in a page as: `const g = useDataGridProTable({...});` then render `<DataGridPro {...g.dataGridProProps} />` from `@mui/x-data-grid-pro`.
 */
export function useDataGridProTable({
  data = [],
  loading,
  isLoading,
  error,
  title = 'table',
  columns: customColumns = [],
  onDataRefresh,
  onRefetch,
  actionButtons,
  initialColumnVisibility = {},
  onColumnVisibilityChange,
  initialSorting = [],
  onRowClick,
  muiTableContainerProps = {},
  enableColumnDragging = false,
  enableColumnOrdering = true,
  enablePinning = true,
  disablePaper = false,
  hideHeader = false,
  /** When true, grid search box is hidden (e.g. page provides its own filter UI). */
  hideQuickFilter = false,
  exportValueTransform,
  searchOnEnter = true,
  getRowId,
  initialPageSize = 1000,
  /** When true, all rows render in one scrollable view (no footer pagination). */
  disablePagination = false,
  rowHeight,
  columnHeaderHeight,
  gridSx,
  /**
   * Pre-populated column filter items (DataGridPro FilterItem[]) restored from
   * persistent storage. Applied once on mount.
   */
  initialFilterItems = [],
  /**
   * Called with the current array of column FilterItems whenever they change.
   * Use this to persist filters to localStorage.
   */
  onColumnFiltersChange,
  /** When false, window Ctrl+F event listener is omitted for secondary tables. */
  enableCtrlF = true,
  /** When true, renders MUI DataGrid checkbox selection column. */
  checkboxSelection = false,
  /** Controlled row selection model (array of IDs, Set of IDs, or GridRowSelectionModel) */
  rowSelectionModel,
  /** Callback fired when row selection changes */
  onRowSelectionModelChange,
  keepNonExistentRowsSelected = false,
}) {
  const effectiveLoading = Boolean(loading ?? isLoading);
  const tableData = useMemo(() => normalizeRows(data), [data]);

  const visibilityStorageKey = `${title}-column-visibility`;
  const orderStorageKey = `${title}-column-order`;
  const filterModelStorageKey = `${title}-filter-model`;

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() =>
    safeLocalRead(visibilityStorageKey, initialColumnVisibility)
  );
  const [columnOrder, setColumnOrder] = useState(() => safeLocalRead(orderStorageKey, []));
  const [quickFilterInput, setQuickFilterInput] = useState('');
  const [quickFilterValue, setQuickFilterValue] = useState('');

  // Controlled filter model — restored from localStorage (without stale quick search) or initialized with initialFilterItems
  const [filterModel, setFilterModel] = useState(() => {
    const saved = safeLocalRead(filterModelStorageKey, null);
    if (saved && typeof saved === 'object') {
      return {
        items: Array.isArray(saved.items) ? saved.items : (Array.isArray(initialFilterItems) ? initialFilterItems : []),
        logicOperator: saved.logicOperator || saved.quickFilterLogicOperator || 'and',
        quickFilterValues: [],
      };
    }
    return {
      items: Array.isArray(initialFilterItems) ? initialFilterItems : [],
      quickFilterValues: [],
      quickFilterLogicOperator: 'and',
    };
  });

  const { denseMode, denseModeTogglePending, persistDenseMode } = useGlobalDenseMode();
  const disableVirtualization = false;
  const effectivePageSize = disablePagination ? Math.max(tableData.length, 1) : initialPageSize;
  const enableStickyPaginationFooter = !disablePagination;
  const resolvedRowHeight = rowHeight ?? (denseMode ? 30 : 38);
  const resolvedColumnHeaderHeight = columnHeaderHeight ?? (denseMode ? 34 : 42);
  const resolvedContainerHeight = muiTableContainerProps?.sx?.height || '100%';

  const [paginationModel, setPaginationModel] = useState({
    pageSize: effectivePageSize,
    page: 0,
  });

  useEffect(() => {
    setPaginationModel((prev) => {
      if (prev.pageSize === effectivePageSize) return prev;
      return { ...prev, pageSize: effectivePageSize };
    });
  }, [effectivePageSize]);

  // Separate Floating Find Bar state (completely independent from DataGrid filterModel)
  const [findSearchTerm, setFindSearchTerm] = useState('');
  const [findMatchIndex, setFindMatchIndex] = useState(0);
  const [isFloatingFindOpen, setIsFloatingFindOpen] = useState(false);
  const floatingInputRef = useRef(null);

  const baseColumns = useMemo(
    () => toDataGridColumns(customColumns, findSearchTerm, tableData),
    [customColumns, findSearchTerm, tableData]
  );

  const orderedColumns = useMemo(() => {
    if (!enableColumnOrdering || columnOrder.length === 0) return baseColumns;
    const byField = new Map(baseColumns.map((c) => [c.field, c]));
    const ordered = columnOrder.map((field) => byField.get(field)).filter(Boolean);
    const remaining = baseColumns.filter((c) => !columnOrder.includes(c.field));
    return [...ordered, ...remaining];
  }, [baseColumns, columnOrder, enableColumnOrdering]);

  /** O(1) row lookup — DataGrid calls getRowId often; indexOf on large arrays is very slow. */
  const rowIndexByRow = useMemo(() => {
    const m = new Map();
    tableData.forEach((row, i) => {
      if (row != null && typeof row === 'object') m.set(row, i);
    });
    return m;
  }, [tableData]);

  // Check if candidate IDs extracted from rows are unique across tableData
  const hasDuplicateCandidateIds = useMemo(() => {
    const seen = new Set();
    for (let i = 0; i < tableData.length; i += 1) {
      const row = tableData[i];
      if (!row || typeof row !== 'object') continue;
      const candidate = row.id ?? row._id ?? row.uuid ?? row.userid ?? row.user_id ?? row.location_id;
      if (candidate != null && candidate !== '') {
        const key = String(candidate);
        if (seen.has(key)) return true;
        seen.add(key);
      }
    }
    return false;
  }, [tableData]);

  const rowIdGetter = useCallback(
    (row) => {
      const rowIndex = rowIndexByRow.get(row);
      const indexArg = rowIndex >= 0 ? rowIndex : undefined;
      if (typeof getRowId === 'function') {
        const custom = getRowId(row, indexArg);
        if (custom != null && custom !== '') return custom;
      }
      const fromRow = row?.id ?? row?._id ?? row?.uuid ?? row?.userid ?? row?.user_id ?? row?.location_id;
      if (fromRow != null && fromRow !== '') {
        if (hasDuplicateCandidateIds && rowIndex >= 0) {
          return `${title}-${fromRow}-${rowIndex}`;
        }
        return fromRow;
      }
      if (rowIndex >= 0) return `${title}-${rowIndex}`;
      return `${title}-row-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    },
    [getRowId, title, rowIndexByRow, hasDuplicateCandidateIds]
  );

  const [internalRowSelectionModel, setInternalRowSelectionModel] = useState(undefined);
  const rawSelectionModel =
    rowSelectionModel !== undefined ? rowSelectionModel : internalRowSelectionModel;

  const gridRowSelectionModel = useMemo(() => {
    if (rawSelectionModel === undefined) return undefined;
    if (
      typeof rawSelectionModel === 'object' &&
      rawSelectionModel !== null &&
      rawSelectionModel.ids instanceof Set &&
      (rawSelectionModel.type === 'include' || rawSelectionModel.type === 'exclude')
    ) {
      return rawSelectionModel;
    }
    if (rawSelectionModel instanceof Set) {
      return { type: 'include', ids: rawSelectionModel };
    }
    if (Array.isArray(rawSelectionModel)) {
      return { type: 'include', ids: new Set(rawSelectionModel) };
    }
    return { type: 'include', ids: new Set() };
  }, [rawSelectionModel]);

  const selectedRowIds = useMemo(() => {
    if (rawSelectionModel === undefined) return [];
    if (Array.isArray(rawSelectionModel)) return rawSelectionModel;
    if (rawSelectionModel instanceof Set) return Array.from(rawSelectionModel);
    if (typeof rawSelectionModel === 'object' && rawSelectionModel !== null && rawSelectionModel.ids instanceof Set) {
      if (rawSelectionModel.type === 'exclude') {
        const excluded = rawSelectionModel.ids;
        return tableData
          .map((r) => rowIdGetter(r))
          .filter((id) => id != null && !excluded.has(id));
      }
      return Array.from(rawSelectionModel.ids);
    }
    return [];
  }, [rawSelectionModel, tableData, rowIdGetter]);

  const handleRowSelectionModelChange = useCallback(
    (newModel, details) => {
      let extractedIds = [];
      if (Array.isArray(newModel)) {
        extractedIds = newModel;
      } else if (newModel instanceof Set) {
        extractedIds = Array.from(newModel);
      } else if (typeof newModel === 'object' && newModel !== null && newModel.ids instanceof Set) {
        if (newModel.type === 'exclude') {
          const excluded = newModel.ids;
          extractedIds = tableData
            .map((r) => rowIdGetter(r))
            .filter((id) => id != null && !excluded.has(id));
        } else {
          extractedIds = Array.from(newModel.ids);
        }
      }

      if (rowSelectionModel === undefined) {
        setInternalRowSelectionModel(newModel);
      }
      if (typeof onRowSelectionModelChange === 'function') {
        onRowSelectionModelChange(extractedIds, newModel, details);
      }
    },
    [rowSelectionModel, onRowSelectionModelChange, tableData, rowIdGetter]
  );

  useEffect(() => {
    safeLocalWrite(visibilityStorageKey, columnVisibilityModel);
    if (typeof onColumnVisibilityChange === 'function') {
      onColumnVisibilityChange(columnVisibilityModel);
    }
  }, [columnVisibilityModel, visibilityStorageKey, onColumnVisibilityChange]);

  useEffect(() => {
    safeLocalWrite(orderStorageKey, columnOrder);
  }, [columnOrder, orderStorageKey]);

  // Clean up any stale quick-filter in storage so it never locks tables
  useEffect(() => {
    try {
      localStorage.removeItem(`${title}-quick-filter`);
    } catch (_) {}
  }, [title]);

  // Save filterModel to localStorage whenever it changes (without persisting transient search query)
  useEffect(() => {
    const { quickFilterValues, ...restModel } = filterModel || {};
    safeLocalWrite(filterModelStorageKey, restModel);
  }, [filterModel, filterModelStorageKey]);

  // Update filterModel.quickFilterValues whenever the committed search value changes.
  useEffect(() => {
    setFilterModel((prev) => ({
      ...prev,
      quickFilterValues: quickFilterValue ? [quickFilterValue.toLowerCase()] : [],
    }));
  }, [quickFilterValue]);

  // Real-time debounced search: update quickFilterValue 150 ms after the user
  // stops typing (no Enter required). When searchOnEnter is explicitly true,
  // the value is only committed on Enter / Search-button click.
  useEffect(() => {
    if (hideQuickFilter || searchOnEnter) return undefined;
    const timer = setTimeout(() => setQuickFilterValue(quickFilterInput), 150);
    return () => clearTimeout(timer);
  }, [quickFilterInput, searchOnEnter, hideQuickFilter]);

  const gridSortModel = useMemo(() => {
    if (!Array.isArray(initialSorting)) return [];
    return initialSorting
      .map((item) => ({
        field: item.id || item.field,
        sort: item.desc ? 'desc' : 'asc',
      }))
      .filter((item) => item.field);
  }, [initialSorting]);

  const handleExport = useCallback(() => {
    const csv = buildCsv(tableData, orderedColumns, exportValueTransform);
    const fileName = `${title || 'table'}-${new Date().toISOString().slice(0, 10)}.csv`;
    downloadCsv(fileName, csv);
  }, [tableData, orderedColumns, exportValueTransform, title]);

  const refresh = onDataRefresh || onRefetch;

  const handleColumnOrderChange = useCallback(
    (params) =>
      setColumnOrder((prev) => {
        const current = prev.length ? prev : orderedColumns.map((c) => c.field);
        const next = [...current];
        const oldIndex = next.indexOf(params.column.field);
        if (oldIndex >= 0) next.splice(oldIndex, 1);
        next.splice(params.targetIndex, 0, params.column.field);
        return next;
      }),
    [orderedColumns]
  );

  // Compute matching row indexes for Floating Find Bar across complete tableData
  const findMatches = useMemo(() => {
    const query = (findSearchTerm || '').trim().toLowerCase();
    if (!query) return [];
    const tokens = query.split(/\s+/).filter(Boolean);
    const matches = [];
    for (let i = 0; i < tableData.length; i += 1) {
      const row = tableData[i];
      if (!row) continue;
      const isMatch = tokens.every((token) =>
        Object.values(row).some((val) => {
          if (val === null || val === undefined) return false;
          return String(val).toLowerCase().includes(token);
        })
      );
      if (isMatch) {
        matches.push(i);
      }
    }
    return matches;
  }, [tableData, findSearchTerm]);

  // Reset match index when findSearchTerm changes
  useEffect(() => {
    setFindMatchIndex(0);
  }, [findSearchTerm]);

  const totalFindMatches = findMatches.length;
  const currentFindMatchPosition = totalFindMatches > 0 ? Math.min(findMatchIndex + 1, totalFindMatches) : 0;

  const handleNextFindMatch = useCallback(() => {
    if (totalFindMatches === 0) return;
    setFindMatchIndex((prev) => (prev + 1) % totalFindMatches);
  }, [totalFindMatches]);

  const handlePrevFindMatch = useCallback(() => {
    if (totalFindMatches === 0) return;
    setFindMatchIndex((prev) => (prev - 1 + totalFindMatches) % totalFindMatches);
  }, [totalFindMatches]);

  const containerRef = useRef(null);
  const apiRef = useGridApiRef();

  // Auto-scroll DataGrid virtual scroller and auto-navigate pagination to target matching row index
  useEffect(() => {
    if (!isFloatingFindOpen || findMatches.length === 0 || findMatchIndex < 0) return;
    const targetRowIndex = findMatches[findMatchIndex];
    if (targetRowIndex == null || targetRowIndex < 0) return;

    const pageSize = paginationModel.pageSize;
    const targetPage = pageSize > 0 && pageSize < tableData.length ? Math.floor(targetRowIndex / pageSize) : 0;

    if (paginationModel.page !== targetPage) {
      setPaginationModel((prev) => ({ ...prev, page: targetPage }));
    }

    const relativeRowIndex = pageSize > 0 && pageSize < tableData.length ? targetRowIndex % pageSize : targetRowIndex;
    const targetTop = relativeRowIndex * resolvedRowHeight;

    const scrollTimer = setTimeout(() => {
      const root = containerRef.current;
      const scroller = root
        ? root.querySelector('.MuiDataGrid-virtualScroller')
        : document.querySelector('.MuiDataGrid-virtualScroller');
      if (scroller) {
        scroller.scrollTop = targetTop;
      }
    }, 60);

    return () => clearTimeout(scrollTimer);
  }, [
    isFloatingFindOpen,
    findMatches,
    findMatchIndex,
    paginationModel.pageSize,
    paginationModel.page,
    resolvedRowHeight,
    tableData.length,
  ]);

  // Close Floating Find Bar and clear find state
  const handleCloseFindBar = useCallback(() => {
    setIsFloatingFindOpen(false);
    setFindSearchTerm('');
    setFindMatchIndex(0);
  }, []);

  // Clear find state if Ctrl+F is disabled for this grid (e.g. modal closed)
  useEffect(() => {
    if (!enableCtrlF && isFloatingFindOpen) {
      setIsFloatingFindOpen(false);
      setFindSearchTerm('');
      setFindMatchIndex(0);
    }
  }, [enableCtrlF, isFloatingFindOpen]);

  // Intercept Ctrl+F / Meta+F on window when component is active
  useEffect(() => {
    if (!enableCtrlF) return undefined;
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        e.stopPropagation();
        setIsFloatingFindOpen(true);
        setTimeout(() => {
          floatingInputRef.current?.focus();
          floatingInputRef.current?.select();
        }, 50);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enableCtrlF]);

  const handleRowClickGrid = useCallback(
    (params) => {
      if (onRowClick) onRowClick(params.row);
    },
    [onRowClick]
  );
  const chrome = (
    <>
      {isFloatingFindOpen && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            top: 70,
            right: 24,
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: '4px 8px 4px 12px',
            borderRadius: '8px',
            bgcolor: 'background.paper',
            boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <TextField
            inputRef={floatingInputRef}
            size="small"
            placeholder="Search in Sales Orders..."
            value={findSearchTerm}
            onChange={(e) => setFindSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                handleCloseFindBar();
              } else if (e.key === 'Enter') {
                e.preventDefault();
                if (e.shiftKey) {
                  handlePrevFindMatch();
                } else {
                  handleNextFindMatch();
                }
              } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                handleNextFindMatch();
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                handlePrevFindMatch();
              }
            }}
            sx={{
              width: 220,
              '& .MuiInputBase-root': { height: 32, fontSize: '0.8125rem' }
            }}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              fontSize: '0.8125rem',
              minWidth: 48,
              textAlign: 'center',
              color: totalFindMatches === 0 && findSearchTerm.trim() ? 'error.main' : 'text.secondary'
            }}
          >
            {findSearchTerm.trim() ? (totalFindMatches > 0 ? `${currentFindMatchPosition}/${totalFindMatches}` : '0/0') : '0/0'}
          </Typography>
          <Tooltip title="Previous match (Shift+Enter or Up Arrow)">
            <span>
              <IconButton size="small" onClick={handlePrevFindMatch} disabled={totalFindMatches === 0}>
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Next match (Enter or Down Arrow)">
            <span>
              <IconButton size="small" onClick={handleNextFindMatch} disabled={totalFindMatches === 0}>
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Close Find Bar (Esc)">
            <IconButton size="small" onClick={handleCloseFindBar}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Paper>
      )}
      {effectiveLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {typeof error === 'string' ? error : error?.message || 'Failed to load table data'}
        </Alert>
      ) : null}
      <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {!hideHeader && (
          <Typography variant="subtitle2" sx={{ mr: 1, fontWeight: 600 }}>
            {title}
          </Typography>
        )}
        {!hideQuickFilter && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TextField
              size="small"
              placeholder="Search..."
              value={quickFilterInput}
              onChange={(e) => setQuickFilterInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setQuickFilterValue(quickFilterInput);
                }
              }}
              InputProps={{
                endAdornment: quickFilterInput ? (
                  <IconButton
                    size="small"
                    aria-label="clear search"
                    onClick={() => {
                      setQuickFilterInput('');
                      setQuickFilterValue('');
                    }}
                    edge="end"
                    sx={{ p: 0.25, mr: -0.5 }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                ) : null,
              }}
              sx={{ minWidth: 220 }}
            />
          </Box>
        )}
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={() => refresh?.()} disabled={effectiveLoading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Export CSV">
          <span>
            <IconButton size="small" onClick={handleExport} disabled={effectiveLoading || tableData.length === 0}>
              <SaveAltIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {Array.isArray(actionButtons) ? actionButtons : (actionButtons || null)}
        <GlobalDenseModeToolbar
          denseMode={denseMode}
          denseModeTogglePending={denseModeTogglePending}
          persistDenseMode={persistDenseMode}
        />
      </Box>
    </>
  );

  // Merge column-filter changes from the grid back into our controlled filterModel,
  // preserving the quick-filter values we manage ourselves.
  const handleFilterModelChange = useCallback(
    (newModel) => {
      setFilterModel((prev) => ({
        ...newModel,
        // Always keep our managed quick-filter values so they aren't wiped by
        // the grid when the user adds/removes column filters.
        quickFilterValues: prev.quickFilterValues,
      }));
      // Notify the caller so it can persist column filters.
      if (typeof onColumnFiltersChange === 'function') {
        onColumnFiltersChange(newModel.items ?? []);
      }
    },
    [onColumnFiltersChange]
  );

  // Case-insensitive, partial-match quick-filter parser.
  // MUI calls this to turn the raw search string into an array of tokens.
  const quickFilterParser = useCallback(
    (searchInput) =>
      searchInput
        .split(' ')
        .filter((word) => word !== '')
        .map((word) => word.toLowerCase()),
    []
  );

  const quickFilterMatcher = useCallback(
    (filterValue) => (cellValue) => {
      if (cellValue === null || cellValue === undefined) return false;
      const cellStr = String(cellValue).toLowerCase();
      return cellStr.includes(String(filterValue).toLowerCase());
    },
    []
  );

  const getRowClassName = useCallback(
    (params) => {
      if (!isFloatingFindOpen || findMatches.length === 0) return '';
      const targetRowIndex = findMatches[findMatchIndex];
      const rowIndex = rowIndexByRow.get(params.row);
      if (rowIndex === targetRowIndex) {
        return 'active-find-match-row';
      }
      return '';
    },
    [isFloatingFindOpen, findMatches, findMatchIndex, rowIndexByRow]
  );

  const dataGridProProps = useMemo(
    () => ({
      apiRef,
      rows: tableData,
      columns: orderedColumns,
      getRowId: rowIdGetter,
      getRowClassName,
      loading: effectiveLoading,
      disableColumnResize: false,
      disableColumnReorder: !enableColumnDragging && !enableColumnOrdering,
      disableRowSelectionOnClick: true,
      checkboxSelection: Boolean(checkboxSelection),
      ...(gridRowSelectionModel !== undefined ? { rowSelectionModel: gridRowSelectionModel } : {}),
      ...(checkboxSelection || onRowSelectionModelChange || rowSelectionModel !== undefined
        ? { onRowSelectionModelChange: handleRowSelectionModelChange }
        : {}),
      keepNonExistentRowsSelected,
      onRowClick: handleRowClickGrid,
      columnVisibilityModel,
      onColumnVisibilityModelChange: setColumnVisibilityModel,
      onColumnOrderChange: handleColumnOrderChange,
      disableVirtualization,
      autoHeight: false,
      pagination: !disablePagination,
      paginationMode: 'client',
      paginationModel,
      onPaginationModelChange: setPaginationModel,
      // Controlled filter model — updates in real time when quickFilterValue changes.
      filterModel,
      onFilterModelChange: handleFilterModelChange,
      // Custom quick-filter behavior: case-insensitive partial match.
      quickFilterParser,
      quickFilterMatcher,
      initialState: {
        sorting: { sortModel: gridSortModel },
        pagination: {
          paginationModel: { pageSize: effectivePageSize, page: 0 },
        },
      },
      pageSizeOptions: [25, 50, 100, 250, 500, 1000, { value: -1, label: 'All' }],
      rowBuffer: 5,
      columnBuffer: 5,
      density: denseMode ? 'compact' : 'standard',
      rowHeight: resolvedRowHeight,
      columnHeaderHeight: resolvedColumnHeaderHeight,
      sx: {
        border: '1px solid',
        borderColor: 'divider',
        scrollBehavior: 'auto',
        '& .MuiDataGrid-virtualScroller': {
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        },
        '& .MuiDataGrid-cell': {
          userSelect: 'text',
        },
        '& .MuiDataGrid-cell ::selection, & .MuiDataGrid-cell::selection': {
          bgcolor: '#ffeb3b !important',
          color: '#000000 !important',
        },
        '& .search-match': {
          backgroundColor: '#ffeb3b !important',
          background: '#ffeb3b !important',
          color: '#000000 !important',
          borderRadius: '2px',
          padding: '0 1px',
          fontWeight: 600,
          display: 'inline',
        },
        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
          outline: 'none',
        },
        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
          outline: 'none',
        },
        '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 700,
        },
        '& .MuiDataGrid-footerContainer': enableStickyPaginationFooter
          ? {
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
          }
          : { display: 'none' },
        ...(gridSx || {}),
      },
    }),
    [
      tableData,
      orderedColumns,
      rowIdGetter,
      getRowClassName,
      effectiveLoading,
      enableColumnDragging,
      enableColumnOrdering,
      enablePinning,
      handleRowClickGrid,
      columnVisibilityModel,
      handleColumnOrderChange,
      filterModel,
      handleFilterModelChange,
      quickFilterParser,
      quickFilterMatcher,
      gridSortModel,
      denseMode,
      enableStickyPaginationFooter,
      disableVirtualization,
      effectivePageSize,
      initialPageSize,
      disablePagination,
      resolvedRowHeight,
      resolvedColumnHeaderHeight,
      gridSx,
      paginationModel,
      checkboxSelection,
      gridRowSelectionModel,
      handleRowSelectionModelChange,
      keepNonExistentRowsSelected,
      onRowSelectionModelChange,
      rowSelectionModel,
    ]
  );

  const selectedRows = useMemo(() => {
    const selectedSet = new Set(selectedRowIds);
    return tableData.filter((row) => selectedSet.has(rowIdGetter(row)));
  }, [tableData, selectedRowIds, rowIdGetter]);

  const outerBoxSx = {
    width: '100%',
    height: resolvedContainerHeight,
    minHeight: 0,
    boxSizing: 'border-box',
    ...(muiTableContainerProps?.sx || {}),
  };

  return {
    chrome,
    dataGridProProps,
    wrapWithPaper: !disablePaper,
    paperSx: {
      p: hideHeader ? 0 : 1,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    outerBoxSx,
    containerRef,
    apiRef,
    /** Exposed so pages can read active column filters (e.g. to render chips). */
    filterModel,
    /** Exposed so pages can programmatically clear/update column filters. */
    setFilterModel,
    /** Exposed row selection state and helper */
    rowSelectionModel: selectedRowIds,
    gridRowSelectionModel,
    setRowSelectionModel: (model) => {
      if (rowSelectionModel === undefined) setInternalRowSelectionModel(model);
      if (typeof onRowSelectionModelChange === 'function') onRowSelectionModelChange(model);
    },
    selectedRows,
  };
}

/** Layout only: renders the MUI X `<DataGridPro />` you pass in (no custom grid wrapper). */
export function MuiXDataGridSurface({ hook, DataGridPro, aboveTable, children }) {
  const inner = (
    <Box ref={hook.containerRef} sx={{ ...hook.outerBoxSx, display: 'flex', flexDirection: 'column' }}>
      {hook.chrome}
      {aboveTable || children}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGridPro {...hook.dataGridProProps} />
      </Box>
    </Box>
  );
  return hook.wrapWithPaper ? <Paper sx={hook.paperSx}>{inner}</Paper> : inner;
}